import React, { useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, ArrowUpRight } from "lucide-react";
import { getVariant, minPrice, resolveImage } from "../data/products";
import { useCatalog } from "../context/CatalogContext";
import ProductImage from "./ProductImage";

/**
 * Horizontally scrollable carousel shown at the bottom of every PDP.
 * Lists every other compound in the catalogue, excluding the current product.
 */
export const RelatedCompounds = ({ currentSlug, productName }) => {
  const { products } = useCatalog();
  const items = useMemo(
    () => products.filter((p) => p.slug !== currentSlug),
    [currentSlug, products]
  );
  const scrollerRef = useRef(null);

  const scroll = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * (216 * 3), behavior: "smooth" });
  };

  if (items.length === 0) return null;

  return (
    <section
      data-testid="related-compounds"
      className="bg-cream-100"
      aria-label="Related compounds"
    >
      <div className="container-x py-12 md:py-16">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <p className="overline mb-2">Related Compounds</p>
            <h2 className="font-display text-2xl md:text-3xl font-bold text-charcoal-600 leading-tight">
              Compounds often studied alongside {productName}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => scroll(-1)}
              data-testid="related-prev"
              aria-label="Previous related compounds"
              className="w-10 h-10 rounded-full border border-[#8B2252]/40 text-[#8B2252] hover:bg-[#8B2252] hover:text-white hover:border-[#8B2252] flex items-center justify-center transition-colors"
            >
              <ChevronLeft className="w-4 h-4" strokeWidth={2} />
            </button>
            <button
              type="button"
              onClick={() => scroll(1)}
              data-testid="related-next"
              aria-label="Next related compounds"
              className="w-10 h-10 rounded-full border border-[#8B2252]/40 text-[#8B2252] hover:bg-[#8B2252] hover:text-white hover:border-[#8B2252] flex items-center justify-center transition-colors"
            >
              <ChevronRight className="w-4 h-4" strokeWidth={2} />
            </button>
          </div>
        </div>

        <div
          ref={scrollerRef}
          className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2 -mx-1 px-1"
          style={{ scrollbarWidth: "none" }}
        >
          {items.map((p) => {
            const defaultVariant = getVariant(p, p.defaultVariant);
            const { src, fallback } = resolveImage(p, defaultVariant);
            const price = minPrice(p);
            const category = (p.category || "")
              .replace(/-/g, " ")
              .replace(/\b\w/g, (c) => c.toUpperCase());
            return (
              <article
                key={p.slug}
                data-testid={`related-card-${p.slug}`}
                className="snap-start shrink-0 w-[200px] rounded-2xl bg-cream-100 border border-charcoal-100/40 overflow-hidden group hover:shadow-[0_18px_36px_-18px_rgba(28,22,24,0.20)] transition-shadow duration-500"
              >
                <Link to={`/product/${p.slug}`} className="block">
                  <div className="aspect-[4/5] overflow-hidden bg-[#FDFAF9]">
                    <ProductImage
                      src={src}
                      fallback={fallback}
                      alt={p.name}
                      className="w-full h-full object-cover [object-position:center_42%] transition-transform duration-500 group-hover:scale-[1.04]"
                      loading="lazy"
                    />
                  </div>
                </Link>
                <div className="p-4 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <Link to={`/product/${p.slug}`}>
                      <h3
                        className="font-serif text-[19px] leading-tight text-charcoal-600 truncate"
                        style={{ fontWeight: 500 }}
                      >
                        {p.name}
                      </h3>
                    </Link>
                    <p className="text-[11px] text-charcoal-400 mt-0.5 truncate">{category}</p>
                    <p className="text-sm font-display font-bold text-charcoal-600 mt-1.5">
                      ${price.toFixed(2)}
                    </p>
                  </div>
                  <Link
                    to={`/product/${p.slug}`}
                    aria-label={`View ${p.name}`}
                    className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white transition-colors"
                    style={{ background: "#8B2252" }}
                  >
                    <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
                  </Link>
                </div>
              </article>
            );
          })}
        </div>
      </div>

      <style>{`
        [data-testid="related-compounds"] ::-webkit-scrollbar { display: none; }
      `}</style>
    </section>
  );
};

export default RelatedCompounds;
